import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";

const HERO_IMG =
  "https://images.pexels.com/photos/9799205/pexels-photo-9799205.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function CustomRugbyUniformsNZ() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Custom Rugby Uniforms NZ",
    serviceType: "Custom rugby uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      areaServed: { "@type": "Country", name: "New Zealand" },
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: ["Auckland", "Wellington", "Christchurch", "Hamilton", "New Zealand"],
    description:
      "Custom rugby uniforms designed and delivered across New Zealand for Pasifika, Māori and community rugby clubs. Sublimated jerseys, shorts, hoodies and warm-ups.",
  };

  return (
    <>
      <SEOHead
        title="Custom Rugby Uniforms NZ | Auckland Studio"
        description="Custom rugby uniforms designed in Auckland and delivered NZ-wide. Pasifika & Māori-inspired sublimated jerseys, shorts and warm-ups for clubs, schools and tournaments."
        path="/custom-rugby-uniforms-nz"
        image={HERO_IMG}
        jsonLd={jsonLd}
      />

      <SEOPageLayout
        heroEyebrow="Auckland · Aotearoa NZ"
        heroH1="Custom Rugby Uniforms in NZ — Built for Your Team"
        heroSub="Premium, fully-custom rugby uniforms designed in Auckland and delivered across New Zealand. Sublimated jerseys, shorts, hoodies and warm-up gear for clubs, schools, churches and tournaments."
        heroImage={HERO_IMG}
        heroImageAlt="Pasifika rugby team in custom uniforms — Auckland NZ"
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Custom Rugby Uniforms NZ" },
        ]}
      >
        <h2>The premier studio for custom rugby uniforms in New Zealand</h2>
        <p>
          From under-12 Saturday morning sides in South Auckland to senior club
          teams running out at provincial finals, Tribal Designz designs and
          manufactures <strong>custom rugby uniforms</strong> that look as good
          on the sideline as they do under the lights. Every kit we produce is
          built around your club's identity — your colours, your values, your
          people. We don't do generic templates. We design with you, for you,
          and for the communities you represent.
        </p>
        <p>
          Based in Auckland, we work with rugby clubs, schools, churches and
          touch teams right across Aotearoa New Zealand. Whether you're a
          Premier 1 club outfitting 60+ players or a touch side ordering 12
          kits for a single tournament, we deliver the same level of design
          care and production quality. Our roots are Pasifika and Māori, and
          that shows up in every kit we make: bold patterns, meaningful
          symbolism, and uniforms that feel like a uniform should — like
          something worth wearing.
        </p>

        <h2>What's included in a Tribal Designz custom rugby kit</h2>
        <p>
          A complete custom rugby package from us typically covers the home
          jersey, alternate (away) jersey, playing shorts, training tops,
          warm-up tees, hoodies and team-issue polos. We also produce coach
          shirts, manager polos and supporter merch so the whole community can
          rep the brand. Everything is sublimated for full-colour designs that
          won't crack, peel or fade — the print is dyed into the fabric, not
          stuck on top of it.
        </p>
        <ul>
          <li>Custom <strong>rugby jerseys</strong> (home + alternate)</li>
          <li>Match shorts &amp; training shorts</li>
          <li>Warm-up tees, hoodies &amp; track jackets</li>
          <li>Coach polos, manager shirts &amp; supporter merch</li>
          <li>Custom-printed playing socks (on request)</li>
          <li>Sublimated cultural patterns, sponsor logos, player names &amp; numbers</li>
        </ul>

        <h2>Built for Pasifika &amp; Māori rugby culture</h2>
        <p>
          Rugby in Aotearoa is more than a sport — it's church, family,
          identity. Our designers specialise in culturally inspired
          uniforms that draw on tatau, tā moko, siapo, masi and traditional
          Polynesian motifs without flattening them into clichés. We work with
          you to understand which symbols are appropriate for your group, what
          stories your kit should tell, and how to balance heritage with a
          modern, professional look that schools and sponsors will respect.
        </p>
        <p>
          That's why teams across South Auckland, West Auckland, Counties
          Manukau and beyond have trusted us to design their <em>custom rugby
          uniforms NZ</em>-wide. We've outfitted senior clubs, school first
          XVs, women's premier sides, touch teams, sevens squads, and church
          rugby groups — and every brief starts with a conversation about who
          you are and who you play for.
        </p>

        <h2>Sublimation, fabric &amp; durability</h2>
        <p>
          All Tribal Designz rugby kits are sublimated on premium 100% poly
          interlock with mesh side panels for breathability. The fabric is
          rated for full-contact rugby — reinforced shoulder seams, double
          stitching at high-stress points, and a tight knit weight that holds
          shape after repeated washing. We also offer heavier "old-school"
          jersey weights for senior clubs that prefer that traditional
          rugby-jersey feel.
        </p>

        <h2>Lead times &amp; minimum order</h2>
        <p>
          Premium custom rugby uniforms have a minimum order of <strong>10
          units</strong> and a typical turnaround of <strong>2–3 weeks</strong>{" "}
          from artwork sign-off to delivery. If you need something faster — say
          for a tournament that snuck up on you — talk to us about our DTF
          fast-print service or expedited rush production. For larger club
          orders (40+ kits), we recommend booking 4–6 weeks out so the
          design and approval process isn't rushed.
        </p>

        <h2>How the design process works</h2>
        <ol>
          <li>
            <strong>Brief.</strong> You send us your team, colours, vibe, any
            existing logos or sponsor requirements, and rough numbers.
          </li>
          <li>
            <strong>Concept.</strong> Within a few days we send back custom
            mockups — usually two or three directions — for your committee or
            captain to review.
          </li>
          <li>
            <strong>Refine &amp; approve.</strong> We tweak the design until
            it's exactly right. You sign off the final artwork.
          </li>
          <li>
            <strong>Sizing run.</strong> We provide a fit kit or sizing chart
            so every player gets the right size.
          </li>
          <li>
            <strong>Produce &amp; deliver.</strong> We manufacture and ship
            anywhere in NZ. Auckland clubs can also collect from our studio.
          </li>
        </ol>

        <h2>Serving rugby teams across Auckland &amp; NZ</h2>
        <p>
          We're proudly Auckland-based but we ship NZ-wide every week. Major
          regions we regularly outfit include:
        </p>
        <ul>
          <li>Auckland Central, North Shore, West Auckland, South Auckland</li>
          <li>Counties Manukau, Waitakere, Manukau, Otahuhu, Mangere</li>
          <li>Hamilton &amp; Waikato, Tauranga, Rotorua, Hawke's Bay</li>
          <li>Wellington &amp; Hutt Valley, Christchurch, Dunedin</li>
        </ul>

        <h2>Related services</h2>
        <p>
          Looking after more than just rugby?{" "}
          <Link to="/custom-uniforms-auckland">Custom uniforms in Auckland</Link>{" "}
          covers our full range across sports.{" "}
          <Link to="/custom-basketball-jerseys-nz">Custom basketball jerseys NZ</Link>{" "}
          for hoops teams.{" "}
          <Link to="/fast-tshirt-printing-auckland">Fast t-shirt printing in Auckland</Link>{" "}
          for events and one-offs. Or read our guide on{" "}
          <Link to="/blog/design-custom-rugby-uniforms-nz">how to design a custom rugby uniform</Link>.
        </p>

        <h2>Ready to design your team's next kit?</h2>
        <p>
          Get a free design concept and quote within 24 hours. No commitment
          until you love what you see. Use the form below or message us on
          WhatsApp to start the conversation.
        </p>
      </SEOPageLayout>
    </>
  );
}
